import type { KpiCardSpec } from '../kpis/card-spec';
import { CHURN_RATE_SCALAR_CARD } from '../kpis/churn-rate-scalar-card';
import { ACCOUNT_DELETIONS_SCALAR_CARD } from '../kpis/account-deletions-scalar-card';
import { ACCOUNT_SWAPS_SCALAR_CARD } from '../kpis/account-swaps-scalar-card';
import { withDashboardLookAll } from '../kpis/with-dashboard-look';

/**
 * Retention breakdown: the churn source's three cards, all shared with the
 * Users page, plus the login-retention rate. Account swaps follows Account
 * deletions, the count it is a subset of.
 */
export const RETENTION_BREAKDOWN_CARDS: KpiCardSpec[] = withDashboardLookAll(
    [
        CHURN_RATE_SCALAR_CARD,
        {
            title: 'Returning user rate',
            source: 'login-retention',
            metric: 'returningUserRate',
            form: 'rate',
            size: 'sm',
            description:
                "Share of the period's active users who already had a login day before it, the rest being new to the login record. Titled for what it measures: the mockup asked for a reactivation rate, meaning users coming back after a stretch of inactivity, and nothing in the backend measures a lapse. A quiet period moves the denominator too, so read the rate against the active-user base beside it.",
        },
        ACCOUNT_DELETIONS_SCALAR_CARD,
        ACCOUNT_SWAPS_SCALAR_CARD,
    ],
    'orange'
);

/**
 * Bottom row: two independent `md` cards, not a group of cards, so this
 * section carries no SectionHeader. Each card owns its own title and subtitle.
 */
export const BOTTOM_ROW_CARDS: KpiCardSpec[] = withDashboardLookAll(
    [
        {
            title: 'Inactivity & return',
            subtitle: 'Combined view',
            source: 'inactivity-return',
            form: 'multiline',
            size: 'md',
            description:
                "Both lines are measured against one common base, the recently active users: one counts those leaving it, the other those coming back into it, so the gap between them reads as a net flow rather than as two unrelated rates. Each point is one day's rate against that day's base, while the legend figure is the period's own rate over distinct users, which is not the average of the points. A returning user is not in that day's base, so the return rate can read above 100% when few users are active. This is not the Churn rate tile above, which reads account deletions net of swaps over the period's signups.",
        },
        {
            title: 'Account deletions',
            subtitle: 'By period',
            source: 'churn',
            metric: 'accountDeletions',
            form: 'line',
            size: 'md',
            description:
                "The same count as the tile above, spread across the period as one point per calendar day instead of summed into a single figure, so a spike or a quiet stretch stays visible. A same-day account re-creation still adds to that day's count; only the churn rate nets it back out.",
        },
    ],
    'orange'
);
